import {
    get,
    limitToLast,
    onChildAdded,
    onChildChanged,
    orderByKey,
    query,
    ref,
} from "firebase/database";
import * as React from "react";
import * as Sentry from "@sentry/react";
import { isEqual } from "lodash";
import { rtdb } from "../../firebase";
import { useFetchUserDetails } from "../../hooks/queries/user";
import { AppNotification } from "../../utils/types/notifications";
import { NotificationContext } from "./NotificationContext";
import { useAppSelector } from "../../hooks/useRedux";

interface NotificationProviderProps {
    children: React.ReactNode;
}

const MAX_NOTIFICATIONS = 20;

type IncomingNotification = Partial<AppNotification> & {
    id?: string;
};

function normalizeNotification(
    notification: IncomingNotification,
    fallbackId?: string
): AppNotification | null {
    const notificationId = notification.id ?? fallbackId;
    if (!notificationId) {
        return null;
    }

    return {
        ...notification,
        id: notificationId,
    } as AppNotification;
}

function getNotificationTimestamp(notification: AppNotification): number {
    const parsedTimestamp = Date.parse(notification.sent_at ?? "");
    return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
}

function sortNotifications(
    notifications: AppNotification[]
): AppNotification[] {
    return [...notifications].sort((firstNotification, secondNotification) => {
        const timestampDiff =
            getNotificationTimestamp(secondNotification) -
            getNotificationTimestamp(firstNotification);
        if (timestampDiff !== 0) {
            return timestampDiff;
        }

        return secondNotification.id.localeCompare(firstNotification.id);
    });
}

function hasNotificationChanged(
    existingNotification: AppNotification,
    newNotification: AppNotification
): boolean {
    if (existingNotification.is_read !== newNotification.is_read) {
        return true;
    }

    return !isEqual(existingNotification, newNotification);
}

export default function NotificationProvider({
    children,
}: NotificationProviderProps) {
    const [userNotifications, setUserNotifications] = React.useState<
        AppNotification[]
    >([]);

    const { data: loggedInUserData } = useFetchUserDetails();

    const { practice } = useAppSelector((state) => state.userPractice);
    const userId = loggedInUserData?.user_id;
    const practiceId = practice?.practice_id;

    const processAppNotifications = React.useCallback(
        (newAppNotifications: AppNotification[]) => {
            setUserNotifications((previousNotifications) => {
                const currentNotifications = [...previousNotifications];
                let notificationListChanged = false;
                newAppNotifications.forEach((newAppNotification) => {
                    const existingNotificationIdx =
                        currentNotifications.findIndex(
                            (u) => u.id === newAppNotification.id
                        );
                    if (
                        existingNotificationIdx > -1 &&
                        !hasNotificationChanged(
                            currentNotifications[existingNotificationIdx],
                            newAppNotification
                        )
                    ) {
                        return;
                    }

                    if (existingNotificationIdx > -1) {
                        currentNotifications.splice(
                            existingNotificationIdx,
                            1,
                            newAppNotification
                        );
                        notificationListChanged = true;
                    } else {
                        currentNotifications.push(newAppNotification);
                        notificationListChanged = true;
                    }
                });
                if (notificationListChanged) {
                    const sortedNotifications =
                        sortNotifications(currentNotifications);
                    return sortedNotifications.slice(0, MAX_NOTIFICATIONS);
                }

                return previousNotifications;
            });
        },
        []
    );

    React.useEffect(() => {
        const resetNotificationsToEmpty = () => {
            setUserNotifications((previousNotifications) => {
                if (previousNotifications.length === 0) {
                    return previousNotifications;
                }

                return [];
            });
        };

        let unsubscribeChildChanged = () => {};
        let unsubscribeChildAdded = () => {};
        let isEffectDisposed = false;

        if (!userId) {
            resetNotificationsToEmpty();
            return () => {};
        }

        if (!practiceId) {
            resetNotificationsToEmpty();
            return () => {};
        }

        const notificationPath = `notifications/${practiceId}/${userId}`;
        const userNotificationRef = query(
            ref(rtdb, notificationPath),
            orderByKey(),
            limitToLast(MAX_NOTIFICATIONS)
        );
        (async () => {
            try {
                const last20UserNotifications = await get(userNotificationRef);
                if (isEffectDisposed) {
                    return;
                }

                if (
                    !last20UserNotifications.exists() ||
                    !last20UserNotifications.hasChildren()
                ) {
                    resetNotificationsToEmpty();
                    return;
                }

                const notifications = Object.entries(
                    last20UserNotifications.val() ?? {}
                )
                    .map(([key, value]) =>
                        normalizeNotification(
                            value as IncomingNotification,
                            key
                        )
                    )
                    .filter(
                        (notification): notification is AppNotification =>
                            notification !== null
                    );
                processAppNotifications(notifications);
            } catch (error) {
                Sentry.captureException(error);
                if (isEffectDisposed) {
                    return;
                }

                resetNotificationsToEmpty();
            }
        })();

        unsubscribeChildChanged = onChildChanged(
            userNotificationRef,
            (snapshot) => {
                if (!snapshot.exists() || isEffectDisposed) {
                    return;
                }

                const notification = normalizeNotification(
                    snapshot.val() as IncomingNotification,
                    snapshot.key ?? undefined
                );
                if (!notification) {
                    return;
                }

                processAppNotifications([notification]);
            }
        );

        unsubscribeChildAdded = onChildAdded(
            userNotificationRef,
            (snapshot) => {
                if (!snapshot.exists() || isEffectDisposed) {
                    return;
                }

                const notification = normalizeNotification(
                    snapshot.val() as IncomingNotification,
                    snapshot.key ?? undefined
                );
                if (!notification) {
                    return;
                }

                processAppNotifications([notification]);
            }
        );

        return () => {
            isEffectDisposed = true;
            unsubscribeChildChanged();
            unsubscribeChildAdded();
        };
    }, [practiceId, processAppNotifications, userId]);
    return (
        <NotificationContext.Provider value={userNotifications}>
            {children}
        </NotificationContext.Provider>
    );
}
